import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { IOffer, IChargeTier, IDiscount } from '@dazn/lp-web/src/types';
import {
    formatWithCurrencyV1,
    getCurrency
} from '@dazn/lp-web/src/utils/dazn/Region';
import {useLandingPagesContext} from '../contexts/LandingPageContext';
import {getOffer} from '../components/ContentTiers/contentTiersUtils';
interface ISavedOfferDetails {
    price?: number | null;
    entitlementSetId?: string | null;
    billingPeriod?: string | null;
    instalments?: any;
    discount: IDiscount | null;
}

export const useSavingPercentage = (
    offerDetail: IOffer | null,
    planSelectorShown = false
) => {
    if (!planSelectorShown) {
        return { savedOffers: null, savingPercentage: null, savingPrice: null };
    }
    
    let otherOfferSaved = useRef(false);
    const {offers, promoOffers, multiplePromoOffers} = useLandingPagesContext();
    
    let otherOffer = getOffer({
        offers,
        promoOffers,
        multiplePromoOffers,
        entitlementSetId: offerDetail ? offerDetail.EntitlementSetId : '',
        billingPeriod:  offerDetail ? offerDetail.BillingPeriod : '',
        billingPlan: offerDetail?.BillingPeriod === 'Instalments' ?  'Annual' : 'Instalments',
        planSelectorShown
    });

    const [savedOffers, setSavedOffers] = useState<ISavedOfferDetails[]>([]);
    const [savingPercentage, setSavingPercentage] = useState<number | null>(
        null
    );
    const [savingPrice, setSavingPrice] = useState<string | number | null>(
        null
    );
    const [savingDiscountedAnnualPrice, setSavingDiscountedAnnualPrice] = useState<string | number | null>(null);

    const calculateSavingsPercentage = useCallback(
        (
            offers: ISavedOfferDetails[]
        ): { savingsPercentage: number; savingPrice: number, savingDiscountedAnnualPrice: number } | null => {
            let hasInstalmentDiscount: boolean = false;
            let hasAnnualDiscount: boolean = false;
            let installmentPrice: number | null | undefined = null;
            let discountedInstallmentPrice: number | null | undefined = null;
            let instalmentFrequency: number = 0;
            let discountedInstalmentFrequency: number = 0;
            let annualPrice: number | null | undefined = null;
            let discountedAnnualPrice: number = 0;

            offers.forEach((offer) => {
                if (offer?.billingPeriod === 'Instalments') {
                    installmentPrice = offer.price;
                    instalmentFrequency = offer.instalments?.TermInMonths;
                    if (offer?.discount) {
                        hasInstalmentDiscount = true;
                        discountedInstallmentPrice = offer?.discount?.Price;
                        discountedInstalmentFrequency = offer?.discount?.DiscountedMonths
                    }
                } else if (offer?.billingPeriod === 'Annual') {
                    annualPrice = offer?.price;
                    if (offer?.discount) {
                        hasAnnualDiscount = true;
                        discountedAnnualPrice = offer?.discount?.Price ?? 0
                    }
                }
            });

            if (installmentPrice === null || annualPrice === null || (discountedInstallmentPrice === null && hasInstalmentDiscount)) {
                return null;
            }

            const discountedInstallmentCost = (discountedInstallmentPrice ?? 0) * discountedInstalmentFrequency
            const afterDiscountedRemainInstallCost = installmentPrice * (instalmentFrequency - discountedInstalmentFrequency)
            const totalDiscountedInstallmentCost = discountedInstallmentCost + afterDiscountedRemainInstallCost
            const totalInstallmentCost = hasInstalmentDiscount ? totalDiscountedInstallmentCost : installmentPrice * instalmentFrequency;
            const savingsPercentage =
                ((totalInstallmentCost - (hasAnnualDiscount ? discountedAnnualPrice : annualPrice)) / (hasAnnualDiscount ? discountedAnnualPrice : annualPrice)) * 100;
            const savingPrice = totalInstallmentCost - (hasAnnualDiscount ? discountedAnnualPrice : annualPrice);
            const savingDiscountedAnnualPrice = annualPrice - (hasAnnualDiscount ? discountedAnnualPrice : annualPrice);
            return { savingsPercentage, savingPrice, savingDiscountedAnnualPrice };
        },
        []
    );
    const currentCurrency = getCurrency();
    const saveOfferDetails = useCallback((offerDetail: IOffer | null) => {
        if (!offerDetail) {
            return;
        }

        const applicableChargeTier = offerDetail.ChargeTiers.find(
            ({ Currency }: IChargeTier) =>
                Currency.toUpperCase() === currentCurrency?.toUpperCase()
        );

        if (applicableChargeTier) {
            const offerObj: ISavedOfferDetails = {
                price: applicableChargeTier.Price,
                entitlementSetId: offerDetail.EntitlementSetId,
                billingPeriod: offerDetail.BillingPeriod,
                instalments: offerDetail?.Instalment,
                discount: applicableChargeTier?.Discount
            };

            setSavedOffers((prevOffers) => {
                const newOffers = [...prevOffers, offerObj];

                const uniqueOffers = newOffers.filter(
                    (item, index, self) =>
                        index ===
                        self.findIndex(
                            (t) =>
                                t.price === item.price &&
                                t.entitlementSetId === item.entitlementSetId &&
                                t.billingPeriod === item.billingPeriod
                        )
                );

                return uniqueOffers;
            });
        }
    }, []);

    if (!otherOfferSaved.current) {
        otherOfferSaved.current = true;
        saveOfferDetails(otherOffer);
    }

    const memoizedSavedOffers = useMemo(() => savedOffers, [savedOffers]);

    useEffect(() => {
        saveOfferDetails(offerDetail);
    }, [offerDetail, saveOfferDetails]);

    useEffect(() => {
        const { savingsPercentage, savingPrice, savingDiscountedAnnualPrice } =
            calculateSavingsPercentage(memoizedSavedOffers) || {};
        if (savingsPercentage && savingPrice) {
            setSavingPercentage(
                savingsPercentage
                    ? Math.ceil(savingsPercentage)
                    : savingsPercentage
            );
            setSavingPrice(
                savingPrice
                    ? formatWithCurrencyV1(savingPrice)
                    : savingPrice
            );
        }
        if(savingDiscountedAnnualPrice){
            setSavingDiscountedAnnualPrice(savingDiscountedAnnualPrice
                ? formatWithCurrencyV1(+savingDiscountedAnnualPrice)
                : savingDiscountedAnnualPrice)
        }
    }, [memoizedSavedOffers, calculateSavingsPercentage]);

    return { savedOffers: memoizedSavedOffers, savingPercentage, savingPrice, savingDiscountedAnnualPrice };
};
